import React  from "react";
import { useDispatch, useSelector } from "react-redux";

import { BasicLinkAction } from "./EllipsisActions";

import useFileSelector from "hooks/useFileSelector";

import { selectedCreativesByMatch } from "selectors/Creatives";

import { exportVersions, valueListMatch } from "reducers/Export";
import { loadCreatives } from "reducers/Creatives";
import {
	importDecisionSheet,
	importVersions
} from "reducers/CreativeVersions";
import { closeModal, openModal } from "reducers/Modal";

import {
	isDynamic,
	hasPermission,
	labelWithCount,
	isAfterEffectsCreative,
	isSocialCreative,
	isVideoBuilderCreative,
	isIncomplete,
	isOpenWebDisplayCreative
} from 'utils/common';
import { CV, MODAL_TYPES } from "utils/constants";

import { BasicAction } from "widgets/ActionMenu";

const hasVersions = ({ numberOfVersions }) => !!numberOfVersions;

export function BasicVersionAction({ component, shouldDisplay, ...props }, rowInfo){
	let handleClick = () => console.log(`"${props.name}" action isn't implemented.`);

	return (
		<BasicAction
			{...props}
			iconName={props.icon}
			handleClick={handleClick}
		/>
	);
}

BasicVersionAction.displayName = "BasicVersionAction";

export const VERSION_LIBRARY = {
	icon:"fas fa-bolt",
	name:"Version Library",
	pathFn:dependentValues => `/library/${dependentValues.libraryId}/creative/${dependentValues.creativeId}/versions/`,
	shouldDisplay:({ user, dependentValues }) => {
		const permittedAction =
				hasPermission(user, CV, "Export Versions")
				||hasPermission(user, CV, "Import Versions")
				||hasPermission(user, CV, "Manage Version Library")
				;
		return isDynamic(dependentValues) && permittedAction;
	},
	component: ({ dependentValues }) => <BasicLinkAction {...VERSION_LIBRARY} rowInfo={dependentValues} />,
};

const ASSIGN_DECISION_TREE_MODERN = {
	name:"Assign Decision Tree (Modern)",
	icon:"dtree",
	isDisabled:true,
	component: () => BasicVersionAction(ASSIGN_DECISION_TREE_MODERN),
};

export const IMPORT_DECISION_SHEET = {
	name:"Import Decision Sheet",
	icon:"fas fa-table",
	shouldDisplay: ({ dependentValues }) => isDynamic(dependentValues) && !isSocialCreative(dependentValues),
	component:({ dependentValues }) => {
		const dispatch = useDispatch();
		const { libraryId, creativeId } = dependentValues;
		const isVideoBuilder = isVideoBuilderCreative(dependentValues);

		let selectFile = useFileSelector(
			(e) => {
				let file = e.currentTarget.files?.item(0);
				if (file) {
					let formData = new FormData();
					formData.append("filename", file);
					formData.append("creativeId", creativeId);
					dispatch(importDecisionSheet(creativeId, formData)).then((resp) => {
						if (resp.success) {
							dispatch(loadCreatives(libraryId));
						}
					});
				}
			},
			{
				accept: ".csv",
			}
		);

		return (
			<BasicAction
				{...IMPORT_DECISION_SHEET}
				isDisabled = {isVideoBuilder}
				iconName="fas fa-table"
				handleClick={selectFile}
			/>
		);
	},
};

export const EXPORT_VERSIONS = {
	name:"Export Versions",
	icon:"download",
	bulkAction:true,
	shouldDisplay:({ user, dependentValues }) => {
		return isDynamic(dependentValues) && hasPermission(user, CV, "Export Versions");
	},
	component:({ dependentValues }) => {
		const dispatch = useDispatch();
		const { creativeId } = dependentValues;
		return (
			<BasicAction
				name = "Export Versions"
				iconName = "download"
				handleClick = {() => {
					dispatch(exportVersions(true, creativeId));
				}}
			/>
		);
	},
};

export const BULK_EXPORT_VERSIONS = {
	name:"Export Versions for Selected Creatives",
	icon:"download",
	bulkAction:true,
	shouldDisplay:({ user, dependentValues }) => {
		return hasPermission(user, CV, "Export Versions");
	},
	component:() => {
		const dispatch = useDispatch();
		const compareFn = creative => isDynamic(creative) && hasVersions(creative);
		const creatives = useSelector(selectedCreativesByMatch(compareFn));
		const creativeIds = creatives.map(ctv => ctv.id);

		async function handleExportVersions() {
			if (creativeIds.length === 1) {
				return dispatch(exportVersions(true, ...creativeIds));
			}

			const resp = await dispatch(valueListMatch(...creativeIds));

			if (resp.success) {
				return dispatch(exportVersions(true, ...creativeIds));
			}

			dispatch(
				openModal(MODAL_TYPES.CONFIRM_BULK_EXPORT_VERSIONS, { creativeIds })
			);
		}

		return (
			<BasicAction
				name = {labelWithCount("Export Versions for Selected Creatives", creativeIds.length)}
				iconName = "download"
				handleClick = {handleExportVersions}
				isDisabled={!creativeIds.length}
			/>
		);
	},
};

export const CREATIVE_IMPORT_VERSIONS_W_PREVIEW = {
	name: "Import Versions with Preview",
	icon: "upload",
	shouldDisplay: ({ user }) => {
		return hasPermission(user, CV, "Import Versions");
	},
	component: ({ dependentValues }) => {
		const dispatch = useDispatch();
		const isVideoBuilder = isVideoBuilderCreative(dependentValues);

		let selectFile = useFileSelector(
			(e) => {
				let file = e.currentTarget.files?.item(0);
				if (file) {
					let formData = new FormData();
					formData.append("file", file);
					dispatch(importVersions(formData, true));
				}
			},
			{
				accept: ".xlsx",
			}
		);

		return (
			<BasicAction
				{...IMPORT_VERSIONS_W_PREVIEW}
				iconName="upload"
				isDisabled = {isVideoBuilder}
				handleClick={selectFile}
			/>
		);
	},
};

export const IMPORT_VERSIONS_W_PREVIEW = {
	name: "Import Versions with Preview",
	icon: "upload",
	shouldDisplay: ({ user, dependentValues }) => {
		const creativeDetails = useSelector(state => state.creativeDetails);

		return [
			hasPermission(user, CV, "Import Versions"),
			!isAfterEffectsCreative(creativeDetails),
			!isSocialCreative(creativeDetails), // Social Creatives do not support import with preview yet
		].every(item => item);
	},
	component:({ dependentValues }) => {
		const dispatch = useDispatch();
		const isVideoBuilder = isVideoBuilderCreative(dependentValues);

		let selectFile = useFileSelector(
			(e) => {
				let file = e.currentTarget.files?.item(0);
				if (file) {
					let formData = new FormData();
					formData.append("file", file);
					dispatch(importVersions(formData, true));
				}
			},
			{
				accept: ".xlsx",
			}
		);

		return (
			<BasicAction
				{...IMPORT_VERSIONS_W_PREVIEW}
				iconName="upload"
				isDisabled = {isVideoBuilder}
				handleClick={selectFile}
			/>
		);
	},
};

export const IMPORT_VERSIONS_W_FASTTRACK = {
	name:"Import Versions w/ FastTrack",
	icon:"upload",
	isDisabled:true,
	shouldDisplay:({ user })=>{return hasPermission(user, CV, "Import Versions");},
	component:() => BasicVersionAction({ ...IMPORT_VERSIONS_W_FASTTRACK }),
};

export const IMPORT_VERSIONS_WITHOUT_PREVIEW = {
	name:"Import Versions without Preview",
	icon:"upload",
	shouldDisplay:({ user })=>{ return hasPermission(user, CV, "Import Versions");},
	component: ({ dependentValues }) => {
		const dispatch = useDispatch();
		const isVideoBuilder = isVideoBuilderCreative(dependentValues);

		let selectFile = useFileSelector(
			(e) => {
				let file = e.currentTarget.files?.item(0);
				if (file) {
					let formData = new FormData();
					formData.append("file", file);
					dispatch(importVersions(formData, false));
				}
			},
			{
				accept: ".xlsx",
			}
		);

		return (
			<BasicAction
				name="Import Versions without Preview"
				iconName="upload"
				isDisabled = {isVideoBuilder}
				handleClick={selectFile}
			/>
		);
	},
};

export const IMPORT_VERSIONS_WITHOUT_PREVIEW_SINGLE_CREATIVE = {
	...IMPORT_VERSIONS_WITHOUT_PREVIEW,
	shouldDisplay: ({ user, dependentValues }) => {
		return isDynamic(dependentValues) && hasPermission(user, CV, "Import Versions");
	},
};

export const IMPORT_VERSIONS_W_PREVIEW_SINGLE_CREATIVE = {
	...IMPORT_VERSIONS_W_PREVIEW,
	shouldDisplay: ({ user, dependentValues }) => {
		return [
			hasPermission(user, CV, "Import Versions"),
			isDynamic(dependentValues),
			!isAfterEffectsCreative(dependentValues),
			!isSocialCreative(dependentValues), // Social Creatives do not support import with preview yet
		].every(item => item);
	},
};

const CANCEL_ALL_PENDING_IMPORTS = {
	name:"Cancel All Pending Imports",
	icon:"fas fa-ban",
	isDisabled:true,
	component:() => BasicVersionAction(CANCEL_ALL_PENDING_IMPORTS),
};

const ASSIGN_DECISION_TREE	= {
	name: "Assign Decision Tree",
	icon: "dtree",
	shouldDisplay:({ user, dependentValues })=> {
		return isDynamic(dependentValues) && hasPermission(user, "Decision Tree", "Assign Decision Tree") && !isSocialCreative(dependentValues);
	},
	component: ({ dependentValues }) => {
		const dispatch = useDispatch()
			, { creativeId, libraryId } = dependentValues;
		const isVideoBuilder = isVideoBuilderCreative(dependentValues);

		return (
			<BasicAction
				{...ASSIGN_DECISION_TREE}
				iconName="dtree"
				isDisabled = {isVideoBuilder}
				handleClick={() => dispatch(openModal(MODAL_TYPES.DECISION_TREE_MODAL, {
					//converting libraryId due to proptypes
					libraryId: libraryId.toString(),
					creativeId,
					onClose: () => dispatch(closeModal()),
				}))}
		  />
		);
	},
};

export const GENERATE_VERSIONS = {
	shouldDisplay: ({ dependentValues, isAiVersionGenerationEnabled }) => {
		return [
			isAiVersionGenerationEnabled,
			isDynamic(dependentValues),
			!isIncomplete(dependentValues),
			isOpenWebDisplayCreative(dependentValues),
		].every(item => item);
	},
	pathFn: (dependentValues) => {
		const { libraryId, creativeId } = dependentValues || {};
		return `/library/${libraryId}/creatives/generate-versions/?creativeIds=${creativeId}`;
	},
	component: ({ dependentValues = {} }) => {
		return (
			<BasicLinkAction
				name="Generate Versions"
				icon="fas fa-wand-magic-sparkles"
				pathFn={GENERATE_VERSIONS.pathFn}
				rowInfo={dependentValues}
			/>
		);
	},
};

export const GENERATE_VERSIONS_FOR_CONCEPT = {
	shouldDisplay: (args) => {
		return GENERATE_VERSIONS.shouldDisplay(args) && args.dependentValues?.conceptId;
	},
	pathFn: (dependentValues) => {
		const { libraryId, conceptId } = dependentValues || {};
		return `/library/${libraryId}/concept/${conceptId}/generate-versions/`;
	},
	component: ({ dependentValues = {} }) => {
		return (
			<BasicLinkAction
				name="Generate Versions for Concept"
				icon="fas fa-wand-magic-sparkles"
				pathFn={GENERATE_VERSIONS_FOR_CONCEPT.pathFn}
				rowInfo={dependentValues}
			/>
		);
	},
};

/**
 * This is the list of Actions used in the Action Menu on the Creative Grid
 */
export const VERSION_ACTION_LIST = [
	[
		VERSION_LIBRARY
	],
	[
		GENERATE_VERSIONS,
		GENERATE_VERSIONS_FOR_CONCEPT,
	],
	[
		ASSIGN_DECISION_TREE,
		IMPORT_DECISION_SHEET,
	],
	[
		EXPORT_VERSIONS,
		IMPORT_VERSIONS_WITHOUT_PREVIEW_SINGLE_CREATIVE,
		IMPORT_VERSIONS_W_PREVIEW_SINGLE_CREATIVE,
	],
];

/**
 * This is the list of actions used in the DTree Modal from the Versions Grid, and Versions Grid toolbar
 */

export const IMPORT_EXPORT_ACTIONS_LIST = [
	IMPORT_VERSIONS_W_PREVIEW,
	IMPORT_VERSIONS_WITHOUT_PREVIEW,
	EXPORT_VERSIONS
];

export const CREATIVES_IMPORT_EXPORT_ACTIONS_LIST = [
	CREATIVE_IMPORT_VERSIONS_W_PREVIEW,
	IMPORT_VERSIONS_WITHOUT_PREVIEW,
	BULK_EXPORT_VERSIONS
];
